import { memo } from 'react'
import { ChevronDownIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import LiveStatusChip from './LiveStatusChip'

/**
 * EntityPanelHeader - Name, category chip, verified badge, last-seen, collapse button.
 * Shown at the top of the expanded panel.
 * @atomic @idempotent
 */
const EntityPanelHeader = memo(function EntityPanelHeader({
  entityDetails,
  isLive,
  localLiveUntil,
  lastSeenText,
  onCollapse,
}) {
  const { t } = useTranslation()

  const address = entityDetails.currentAddress || entityDetails.current_address
  const lat = entityDetails.currentLatitude || entityDetails.current_latitude
  const lng = entityDetails.currentLongitude || entityDetails.current_longitude
  const mapsHref = lat && lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null

  return (
    <div className="flex items-start justify-between px-2 pt-2 pb-1">
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-gray-900 leading-tight">{entityDetails.name}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {entityDetails.category && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {entityDetails.category.replace('_', ' ')}
            </span>
          )}
          <LiveStatusChip isLive={isLive} liveUntil={localLiveUntil} />
          {entityDetails.is_verified && (
            <span className="flex items-center gap-0.5 text-[11px] text-green-600 font-medium whitespace-nowrap">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('entity.isVerified', 'Verified')}
            </span>
          )}
          {lastSeenText && (
            <span className="text-xs text-gray-500 flex items-center gap-0.5">
              <ClockIcon className="w-3 h-3" />{lastSeenText}
            </span>
          )}
        </div>
        {address && mapsHref && (
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 mt-1 text-xs text-gray-500 hover:text-primary transition-colors group"
          >
            <MapPinIcon className="w-3 h-3 flex-shrink-0 text-gray-400 group-hover:text-primary" />
            <span className="truncate leading-snug">{address}</span>
          </a>
        )}
      </div>
      <button
        onClick={() => onCollapse('semi')}
        className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors ml-1"
        aria-label={t('common.collapseDetails', 'Collapse details')}
      >
        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
      </button>
    </div>
  )
})

export default EntityPanelHeader
